import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import Zone from "@insite/client-framework/Components/Zone";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import { useGetLink } from "@insite/client-framework/Store/Links/LinksSelectors";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Button from "@insite/mobius/Button";
import { BaseTheme } from "@insite/mobius/globals/baseTheme";
import Hidden from "@insite/mobius/Hidden";
import Icon from "@insite/mobius/Icon";
import Link from "@insite/mobius/Link";
import Tab from "@insite/mobius/Tab";
import TabGroup, { TabGroupPresentationProps } from "@insite/mobius/TabGroup";
import Typography from "@insite/mobius/Typography";
import { breakpointMediaQueries, injectCss } from "@insite/mobius/utilities";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import uniqueId from "@insite/mobius/utilities/uniqueId";
import CustomApplicationState from "@tSurya/Store/Data/CustomApplicationState/CustomApplicationState";
import StyledLink from "@tSurya/theme/components/StyledLink";
import { COLORS, TYPOGRAPHY } from "@tSurya/theme/design";
import React, { useState } from "react";
import { connect } from "react-redux";
import { Link as RouterLink } from "react-router-dom";
import { css } from "styled-components";
import styled from "styled-components";
import { isSuryaWebsite } from "@tSurya/Common/Utilities/websiteUtils";

const enum fields {
    title = "title",
    numberOfTabs = "numberOfTabs",
    linkText = "linkText",
    linkUrl = "linkUrl",
    openInNewWindow = "openInNewWindow",
    linkTextColor = "linkTextColor",
    cssClass = "cssClass",
}

interface NameArray {
    name: string;
    tabId: string;
}

interface TabModel {
    fields: NameArray;
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.numberOfTabs]: TabModel[];
        [fields.linkText]: string;
        [fields.linkUrl]: LinkFieldValue;
        [fields.openInNewWindow]: boolean;
        [fields.linkTextColor]: string;
        [fields.cssClass]: string;
    };
}

const mapStateToProps = (state: CustomApplicationState) => {
    return {
        headerHeight: state.SURData.headerHeightStore.headerHeight,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>; 

export interface SurTabularDataStyles {
    tabView?: InjectableCss;
    tabTitle?: InjectableCss;
    tabGroup?: TabGroupPresentationProps;
    linkContainer?: InjectableCss;
}
export const tabularDataStyles: SurTabularDataStyles = {
    tabView: {
        css: css`
            background: ${getColor("common.backgroundContrast")};
            padding: 8px 12px;
            font-style: normal;
            min-width: auto;
            font-weight: ${TYPOGRAPHY.weight.base};
            font-size: ${TYPOGRAPHY.size.small};
            line-height: ${TYPOGRAPHY.lineHeight.msmall};
            transition: none !important;
            color: ${getColor("text.main")};
            cursor: pointer;
            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    css`
                        padding: 12px;
                        background: ${getColor("secondary")};
                    `,
                    null,
                    null,
                ])}
            &[aria-selected="false"] {
                border-bottom: 2px solid transparent;
            }
            &[aria-selected="true"] {
                span {
                    color: ${getColor("text.main")};
                    font-weight:700;
                }

                border-bottom: 2px solid ${getColor("text.main")};
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid ${getColor("text.link")};
                        `,
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid ${getColor("text.link")};
                        `,
                        css`
                            span {
                                color: ${getColor("text.main")};
                            }
                            border-bottom: 2px solid ${getColor("text.link")};
                        `,
                        null,
                        null,
                    ])}
            }
        `,
    },
    tabTitle: {
        css: css`
            font-family: ${TYPOGRAPHY.widgetTitle.fontFamily};
            font-style: ${TYPOGRAPHY.widgetTitle.fontStyle};
            font-weight: ${TYPOGRAPHY.widgetTitle.weight};
            font-size: ${TYPOGRAPHY.widgetTitle.size};
            line-height: ${TYPOGRAPHY.widgetTitle.lineHeight};
            color: ${getColor("text.main")};
            /* identical to box height, or 117% */
            margin-bottom: 24px;
            text-align: center;
            text-transform: ${TYPOGRAPHY.widgetTitle.textTransform};

            margin-bottom: 24px;
                ${isSuryaWebsite() && css`
                    margin-top: 60px;
                `}

            ${({ theme }: { theme: BaseTheme }) =>
                breakpointMediaQueries(theme, [
                    css`
                        font-size: ${TYPOGRAPHY.widgetTitle.mobileSize};
                        ${isSuryaWebsite() && css`
                            margin-top: 48px;
                    `   }
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.widgetTitle.mobileSize};
                    `,
                    css`
                        font-size: ${TYPOGRAPHY.widgetTitle.mobileSize};
                    `,
                    null,
                    null,
                ])}
        `,
    },
    tabGroup: {
        cssOverrides: {
            tabGroup: css`
                align-items: center;
                justify-content: flex-start;
                gap: 16px;
                z-index: 9;
                margin: 0 auto;
                margin-bottom: 20px;
                ${({ theme }: { theme: BaseTheme }) =>
                    breakpointMediaQueries(theme, [
                        null,
                        null,
                        css`
                            justify-content: center;
                        `,
                        css`
                            justify-content: center;
                        `,
                        css`
                            justify-content: center;
                        `,
                    ])}
                &::-webkit-scrollbar{
                    width:0;
                }
               
                > li > span {
                    font-family: ${TYPOGRAPHY.badge.fontFamily};
                    font-weight: ${TYPOGRAPHY.badge.weight};
                    color: ${getColor("text.main")};
                    font-size: ${TYPOGRAPHY.badge.size};
                    line-height: ${TYPOGRAPHY.badge.lineHeight};
                    text-transform: ${TYPOGRAPHY.badge.textTransform};

                    ${({ theme }: { theme: BaseTheme }) =>
                        breakpointMediaQueries(theme, [
                            css`
                            `,
                            css`
                            `,
                            css`
                            `,
                            null,
                            null,
                        ])}
                }
            `,
            tabContent: css`
                border: none;
                padding: 0px !important;
            `,
        },
    },
    linkContainer: {
        css: css`
            display: flex;
            justify-content: center;
        `,
    },
};

const styles = tabularDataStyles;

const SURTabularWidget: React.FC<Props> = ({ fields, id,headerHeight  }) => {
    const [searchedTextState, setSearchedTextState] = useState("");
    const [defaultTabState, setDefaultTabState] = useState(fields.numberOfTabs[0]?.fields.name);
    const { url } = useGetLink(fields.linkUrl);

    React.useEffect(() => {
        if (typeof window !== "undefined") {
        
            const searchParameter= new URLSearchParams(window?.location?.search);

            const tabIdParameter = searchParameter.get("tabId");

            if (tabIdParameter) {
                setSearchedTextState(tabIdParameter);
            }

        }
    }, []);


    React.useEffect(() => {
        if (typeof window !== undefined) {
            if (searchedTextState !== "") {

                const matchingTab = fields.numberOfTabs.find(tab => {
                    return tab.fields.tabId === searchedTextState;
                });

                if (matchingTab !== undefined && matchingTab.fields.tabId !== "") {
                    setDefaultTabState(matchingTab.fields.name);
                }
            }
        }
    }, [searchedTextState]);

    React.useEffect(() => {
        if (typeof window !== undefined && searchedTextState !== "") {
            const matchedTextId = document.getElementById(searchedTextState);
            if (matchedTextId) {
                window?.scrollTo({
                    top: matchedTextId?.offsetTop - headerHeight!,
                });
            }
        }
    }, [defaultTabState,headerHeight]);

    return (
        <StyledWrapper>
            <Typography as="h2" {...styles.tabTitle}>
                {fields.title}
            </Typography>
            <TabGroup current={defaultTabState} {...styles.tabGroup} aria-busy="true">
                {fields.numberOfTabs.map((numberOfTab, index) => {
                    return (
                        <Tab
                            headline={numberOfTab.fields.name}
                            tabKey={numberOfTab.fields.name}
                            {...styles.tabView}
                            // eslint-disable-next-line react/no-array-index-key
                            key={index}
                            aria-busy="true"
                            tabIndex={0}
                            id={numberOfTab?.fields?.tabId?.toLowerCase()}
                        >
                            <Zone contentId={id} zoneName={numberOfTab.fields.name} />
                        </Tab>
                    );
                })}
            </TabGroup>
            
            {fields.linkText && url && (
                <StyledWrapper {...styles.linkContainer}>
                    <StyledLink
                        href={url}
                        target={fields.openInNewWindow ? "_blank" : undefined}
                        rel={fields.openInNewWindow ? "noopener noreferrer" : undefined}
                        textColor={fields.linkTextColor}
                        aria-label={fields.linkText}
                    >
                        {fields.linkText}
                    </StyledLink>
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

const surTabularWidget: WidgetModule = {
    component: connect(mapStateToProps)(SURTabularWidget),
    definition: {
        group: "Common",
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
            {
                name: fields.numberOfTabs,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields) => {
                    return item.fields.name;
                },
                defaultValue: [],
                fieldType: "General",
                sortOrder: 1,
                fieldDefinitions: [
                    {
                        name: "name",
                        displayName: "Tab Name",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                    {
                        name: "tabId",
                        displayName: "Tab Id",
                        editorTemplate: "TextField",
                        defaultValue: "",
                    },
                ],
            },
            {
                fieldType: "Translatable",
                name: fields.linkText,
                displayName: "Link Text",
                editorTemplate: "TextField",
                defaultValue: "",
                sortOrder: 2,
            },
            {
                name: fields.linkUrl,
                displayName: "Link URL",
                editorTemplate: "LinkField",
                defaultValue: { type: "Page", value: "" },
                fieldType: "General",
                sortOrder: 3,
            },
            {
                name: fields.openInNewWindow,
                displayName: "Open Link in New Window",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                sortOrder: 4,
            },
            {
                name: fields.linkTextColor,
                displayName: "Link Text Color",
                editorTemplate: "ColorPickerField",
                defaultValue: "",
                fieldType: "General",
                sortOrder: 5,
            },
            {
                name: fields.cssClass,
                displayName: "Add CSS Class",
                editorTemplate: "TextField",
                defaultValue: "",
                fieldType: "General",
            },
        ],
    },
};
export default surTabularWidget;
